import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useStore } from '../../../Context/context.jsx';
import { Icon } from '@iconify/react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getData } from '../../../webService/webservice';
import { getDateRange } from "../../../utils/get-DateRange.js";
import '../../../styles/Auth/appointment-report.css';
import { exportDataToExcel } from "../../../utils/excel-Export.js";

const AppointmentReport = () => {
    const context = useOutletContext() || {};
    const { startDate: contextStartDate, endDate: contextEndDate } = context;
    const { selectedStoreId } = useStore();
    const sessionToken = localStorage.getItem('sessionToken');

    const [data, setData] = useState([]);
    const [summary, setSummary] = useState({
        totalAppointments: 0,
        totalServiceValue: 0,
        avgAppointmentValue: 0,
        completionRate: 0,
        cancellationRate: 0
    });
    const role = localStorage.getItem("role");
    const adminStoreIds = JSON.parse(localStorage.getItem("storeIds") || "[]");
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('overall');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [tableSearch, setTableSearch] = useState('');
    const [dateRange, setDateRange] = useState("Today");

    const [startDate, setStartDate] = useState(contextStartDate || new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(contextEndDate || new Date().toISOString().split('T')[0]);

    // Sync with context if it changes (Global Filter)
    useEffect(() => {
        if (contextStartDate) setStartDate(contextStartDate);
        if (contextEndDate) setEndDate(contextEndDate);
    }, [contextStartDate, contextEndDate]);
    const [filters, setFilters] = useState({
        staffId: '',
        status: '',
        customerId: '',
        clientPhone: '',
        storeId: '',
        serviceId: ''
    });

    const [appliedFilters, setAppliedFilters] = useState({
        startDate: contextStartDate || new Date().toISOString().split('T')[0],
        endDate: contextEndDate || new Date().toISOString().split('T')[0],
        staffId: '',
        status: '',
        customerId: '',
        clientPhone: '',
        storeId: '',
        serviceId: ''
    });

    const [staffList, setStaffList] = useState([]);
    const [customerList, setCustomerList] = useState([]);
    const [storeList, setStoreList] = useState([]);
    const [serviceList, setServiceList] = useState([]);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    const [showColumnToggler, setShowColumnToggler] = useState(false);

    const [visibleColumns, setVisibleColumns] = useState({
        // OVERALL
        appointmentDate: true,
        clientName: true,
        clientPhone: true,
        branchName: true,
        therapistName: true,
        servicesProvided: true,
        totalDuration: true,
        serviceValue: true,
        status: true,

        // THERAPIST WISE
        totalAppointments: true,
        totalServiceCount: true,
        completedCount: true,

        // SERVICE WISE
        serviceName: true,
        bookedCount: true,
        totalValue: true,

        // COMMON
        action: true
    });

    const columnTogglerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (columnTogglerRef.current && !columnTogglerRef.current.contains(event.target)) {
                setShowColumnToggler(false);
            }
        };

        if (showColumnToggler) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showColumnToggler]);

    const TAB_COLUMNS = {
        overall: [
            'appointmentDate',
            'clientName',
            'clientPhone',
            'branchName',
            'therapistName',
            'servicesProvided',
            'totalDuration',
            'serviceValue',
            'status',
            'action'
        ],

        'therapist-wise': [
            'therapistName',
            'totalAppointments',
            'totalServiceCount',
            'serviceValue',
            'completedCount',
            'action'
        ],

        'service-wise': [
            'serviceName',
            'bookedCount',
            'totalValue',
            'action'
        ],

        'status-wise': [
            'servicesProvided',
            'status',
            'clientName',
            'action'
        ]
    };

    const COLUMN_LABELS = {
        appointmentDate: 'Date & Time',
        clientName: 'Client Name',
        clientPhone: 'Phone Number',
        branchName: 'Branch',
        therapistName: 'Therapist Name',
        servicesProvided: 'Service Name',
        totalDuration: 'Duration (min)',
        serviceValue: 'Service Value',
        status: 'Service Status',
        action: 'Action',
        totalAppointments: 'Total Appointments',
        totalServiceCount: 'Total Service Count',
        completedCount: 'Completed Count',
        serviceName: 'Service Name',
        categoryName: 'Service Category',
        bookedCount: 'Booked Count',
        totalValue: 'Total Value',
        count: 'Count',
        value: 'Value',

    };

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'Pending', label: 'Pending' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Cancelled', label: 'Cancelled' }
    ];

    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            height: '44px',
            minHeight: '44px',
            background: '#f8fafc',
            borderColor: state.isFocused ? '#3c0008' : '#e2e8f0',
            borderRadius: '12px',
            padding: '0 4px',
            fontSize: '14px',
            color: '#1e293b',
            boxShadow: 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#3c0008' : '#cbd5e1',
            }
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 8px',
        }),
        singleValue: (base) => ({
            ...base,
            color: '#1e293b',
        }),
        placeholder: (base) => ({
            ...base,
            color: '#64748b',
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 999999
        }),
        menu: (base) => ({
            ...base,
            zIndex: 999999,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        }),
        option: (base, state) => ({
            ...base,
            fontSize: '14px',
            backgroundColor: state.isSelected ? '#3c0008' : state.isFocused ? '#f1f5f9' : '#fff',
            color: state.isSelected ? '#fff' : '#1e293b',
            '&:active': {
                backgroundColor: '#3c0008',
                color: '#fff',
            }
        })
    };

    const safeExtractArray = (res) => {

        if (!res || !res.data) return [];

        // Direct array
        if (Array.isArray(res.data)) {

            // Handle paginatedResults structure
            if (
                res.data.length > 0 &&
                res.data[0]?.paginatedResults
            ) {
                return res.data[0].paginatedResults;
            }

            return res.data;
        }

        // Object with paginatedResults
        if (Array.isArray(res.data.paginatedResults)) {
            return res.data.paginatedResults;
        }

        // Any nested array fallback
        for (let key in res.data) {
            if (Array.isArray(res.data[key])) {
                return res.data[key];
            }
        }

        return [];
    };

    const fetchDropdownData = useCallback(async (targetStoreId) => {
        try {
            const commonParams = { limit: 10000 };
            if (targetStoreId) commonParams.storeId = targetStoreId;

            const [staffRes, storeRes, customerRes, serviceRes] = await Promise.all([
                getData('users/users', commonParams, sessionToken),
                getData('stores/stores', { limit: 10000 }, sessionToken),
                getData('customers/customers', commonParams, sessionToken),
                getData('services/services', commonParams, sessionToken)
            ]);

            setStaffList(safeExtractArray(staffRes));
            setStoreList(safeExtractArray(storeRes));
            setCustomerList(safeExtractArray(customerRes));
            setServiceList(safeExtractArray(serviceRes));
        } catch (error) {
            console.error('Error fetching dropdown data', error);
        }
    }, [sessionToken]);

    const [debouncedFilters, setDebouncedFilters] = useState(filters);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedFilters(filters);
        }, 300);
        return () => clearTimeout(handler);
    }, [filters]);

    const fetchReportData = useCallback(async () => {
        try {
            const params = {
                startDate,
                endDate,
                tab: activeTab,
                page: currentPage,
                limit: rowsPerPage,
                search: tableSearch,
                ...debouncedFilters
            };

            if (!params.storeId && selectedStoreId) {
                params.storeId = selectedStoreId;
            }

            const response = await getData('reports/reports/appointment-sales', params, sessionToken);
            if (response?.success || response?.status === 200) {
                setData(response.data?.data || []);
                setSummary(response.data?.summary || {});
                setTotalItems(response.data?.pagination?.totalItems || 0);
            }
        } catch (error) {
            toast.error('Failed to fetch appointment report');
        } finally {
            setLoading(false);
        }
    }, [debouncedFilters, startDate, endDate, activeTab, currentPage, rowsPerPage, tableSearch, selectedStoreId, sessionToken]);

    // Unified data fetching effect
    useEffect(() => {
        const delay = tableSearch ? 500 : 50;
        const handler = setTimeout(() => {
            fetchReportData();
        }, delay);
        return () => clearTimeout(handler);
    }, [debouncedFilters, startDate, endDate, activeTab, selectedStoreId, currentPage, rowsPerPage, tableSearch, fetchReportData]);

    useEffect(() => {
        fetchDropdownData(filters.storeId || selectedStoreId);
    }, [filters.storeId, selectedStoreId, fetchDropdownData]);

    const resetFilters = () => {
        const today = new Date().toISOString().split('T')[0];
        setStartDate(today);
        setEndDate(today);
        setFilters({
            staffId: '',
            status: '',
            customerId: '',
            clientPhone: '',
            storeId: '',
            serviceId: ''
        });
        setTableSearch('');
        setCurrentPage(1);
    };



    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return '#e6fffa';
            case 'Scheduled': return '#ebf8ff';
            case 'Cancelled': return '#fff5f5';
            case 'Pending': return '#fffaf0';
            case 'No-Show': return '#f7fafc';
            default: return '#f8fafc';
        }
    };

    const getStatusTextColor = (status) => {
        switch (status) {
            case 'Completed': return '#319795';
            case 'Scheduled': return '#3182ce';
            case 'Cancelled': return '#e53e3e';
            case 'Pending': return '#dd6b20';
            case 'No-Show': return '#4a5568';
            default: return '#1e293b';
        }
    };
    const isSuperAdmin = role === "SuperAdmin" || role === "SelfAdmin";

    // For non-superadmins: only show stores they are assigned to
    const filteredStoreList = isSuperAdmin
        ? storeList
        : storeList.filter(s => adminStoreIds.includes(s._id));

    // Show branch filter for super-admins OR any user who manages multiple stores
    const showBranchFilter = isSuperAdmin || filteredStoreList.length > 1;

    const totalPages = Math.ceil(totalItems / rowsPerPage);

    const handleExport = () => {

        if (!data.length) {
            return toast.info("No data to export");
        }

        let exportData = [];

        switch (activeTab) {

            case "overall":

                exportData = data.map((item) => ({
                    "Appointment Date":
                        item.timeSlot
                            ? new Date(item.timeSlot).toLocaleString("en-IN")
                            : item.appointmentDate
                                ? new Date(item.appointmentDate).toLocaleString("en-IN")
                                : "",

                    "Client Name": item.customerName || "",
                    "Phone Number": item.customerPhone || item.clientPhone || "",
                    "Branch": item.branchName || "",

                    "Therapist":
                        Array.isArray(item.employeeNames)
                            ? item.employeeNames.join(", ")
                            : item.employeeNames || "",

                    "Services":
                        Array.isArray(item.serviceNames)
                            ? item.serviceNames.join(", ")
                            : item.serviceNames || "",

                    "Duration (Min)": item.totalDuration || 0,
                    "Service Value": item.totalServiceValue || 0,
                    "Status": item.status || ""
                }));

                break;

            case "therapist-wise":

                exportData = data.map((item) => ({
                    "Therapist Name": item.therapistName || "",
                    "Total Appointments": item.totalAppointments || 0,
                    "Total Service Count": item.totalServiceCount || 0,
                    "Completed Count": item.completedCount || 0,
                    "Service Value": item.totalServiceValue || 0
                }));

                break;

            case "service-wise":

                exportData = data.map((item) => ({
                    "Service Name": item.serviceName || "",
                    "Category": item.categoryName || "",
                    "Booked Count": item.bookedCount || 0,
                    "Total Value": item.totalValue || 0
                }));

                break;

            case "status-wise":

                exportData = data.map((item) => ({
                    "Client Name": item.customerName || "",
                    "Service Name":
                        Array.isArray(item.serviceNames)
                            ? item.serviceNames.join(", ")
                            : item.serviceNames || "",

                    "Status": item.status || ""
                }));

                break;

            default:
                exportData = data;
        }

        const fileName =
            `Appointment_Report_${activeTab}_${startDate}_to_${endDate}`;

        exportDataToExcel(
            exportData,
            "Appointment Report",
            `${fileName}.xlsx`
        );
    };

    return (
        <div className='product-layout'>
            <div className='product-content'>
                <main className='product-main'>
                    <div className="sales-report-container">

                        {/* ── Header ── */}
                        <div className="sales-report-standalone-header">
                            <div className="header-left">
                                <h1 className="page-title">Appointment Report</h1>
                                <p className="page-subtitle">Track and analyze appointment bookings and staff performance</p>
                            </div>
                            <div className="header-right">
                                <button className="export-dropdown-btn" onClick={handleExport}>
                                    <Icon icon="mdi:export-variant" width="18" /> Export <Icon icon="mdi:chevron-down" />
                                </button>
                            </div>
                        </div>

                        {/* ── Tabs ── */}
                        <div className="sales-report-tabs-wrapper">
                            <div className="sales-report-tabs">
                                {Object.keys(TAB_COLUMNS).map(tab => (
                                    <button
                                        key={tab}
                                        className={`sales-report-tab ${activeTab === tab ? 'active' : ''}`}
                                        onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                                    >
                                        {tab.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* ── Filters ── */}
                        <div className="sales-filter-bar">
                            <div className="filter-grid">
                                {/* Date Range */}
                                <div className="filter-item">
                                    <label>Date Range</label>
                                    <div className="date-range-picker-combined">
                                        <select
                                            className="filter-select date-range-select"
                                            value={dateRange}
                                            onChange={e => {
                                                const range = e.target.value;
                                                setDateRange(range);
                                                const { startDate: s, endDate: eD } = getDateRange(range, startDate, endDate);
                                                setStartDate(s);
                                                setEndDate(eD);
                                            }}
                                        >
                                            <option value="Today">Today</option>
                                            <option value="Yesterday">Yesterday</option>
                                            <option value="Last 7 Days">Last 7 Days</option>
                                            <option value="Last Month">Last Month</option>
                                            <option value="Current Month">Current Month</option>
                                            <option value="Current FY">Current Financial Year</option>
                                            <option value="Previous FY">Previous Financial Year</option>
                                            <option value="Custom">Custom Date</option>
                                        </select>
                                        {dateRange === "Custom" ? (
                                            <div className="custom-date-inputs">
                                                <input type="date" className="filter-input date-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
                                                <span className="date-separator">to</span>
                                                <input type="date" className="filter-input date-input" value={endDate} onChange={e => setEndDate(e.target.value)} />
                                            </div>
                                        ) : (
                                            <div className="date-display-box">
                                                <Icon icon="mdi:calendar-outline" width="18" />
                                                <span>{startDate === endDate ? startDate : `${startDate} – ${endDate}`}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Client */}
                                {(activeTab === 'overall' || activeTab === 'status-wise') && (
                                    <div className="filter-item">
                                        <label>Client Name</label>
                                        <Select
                                            options={[{ value: '', label: 'All Clients' }, ...(Array.isArray(customerList) ? customerList.map(c => ({ value: c._id, label: c.name || c.customerName })) : [])]}
                                            value={filters.customerId ? { value: filters.customerId, label: customerList?.find(c => c._id === filters.customerId)?.name || 'Select Client' } : null}
                                            onChange={opt => setFilters(prev => ({ ...prev, customerId: opt?.value || '' }))}
                                            placeholder="Select Client..."
                                            isClearable isSearchable
                                            styles={customSelectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                )}

                                {/* Therapist */}
                                {(activeTab === 'overall' || activeTab === 'therapist-wise') && (
                                    <div className="filter-item">
                                        <label>Therapist</label>
                                        <Select
                                            options={[{ value: '', label: 'All Therapists' }, ...staffList.map(s => ({ value: s._id, label: s.name }))]}
                                            value={filters.staffId ? { value: filters.staffId, label: staffList.find(s => s._id === filters.staffId)?.name } : null}
                                            onChange={opt => setFilters(prev => ({ ...prev, staffId: opt?.value || '' }))}
                                            styles={customSelectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            placeholder="Select Therapist..."
                                        />
                                    </div>
                                )}

                                {/* Branch */}
                                {activeTab === 'overall' && showBranchFilter && (
                                    <div className="filter-item">
                                        <label>Branch</label>
                                        <Select
                                            options={[{ value: '', label: 'All Branches' }, ...filteredStoreList.map(s => ({ value: s._id, label: s.storeName }))]}
                                            value={filters.storeId ? { value: filters.storeId, label: filteredStoreList.find(s => s._id === filters.storeId)?.storeName } : null}
                                            onChange={opt => setFilters(prev => ({ ...prev, storeId: opt?.value || '' }))}
                                            styles={customSelectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            placeholder="Select Branch..."
                                        />
                                    </div>
                                )}

                                {/* Service */}
                                {(activeTab === 'overall' || activeTab === 'service-wise' || activeTab === 'status-wise') && (
                                    <div className="filter-item">
                                        <label>Service</label>
                                        <Select
                                            options={[{ value: '', label: 'All Services' }, ...(serviceList || []).map(s => ({ value: s._id, label: s.serviceName }))]}
                                            value={filters.serviceId ? { value: filters.serviceId, label: serviceList.find(s => s._id === filters.serviceId)?.serviceName } : null}
                                            onChange={opt => setFilters(prev => ({ ...prev, serviceId: opt?.value || '' }))}
                                            styles={customSelectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            placeholder="Select Service..."
                                        />
                                    </div>
                                )}

                                {/* Status */}
                                {(activeTab === 'overall' || activeTab === 'status-wise') && (
                                    <div className="filter-item">
                                        <label>Appointment Status</label>
                                        <Select
                                            options={statusOptions}
                                            value={statusOptions.find(o => o.value === filters.status)}
                                            onChange={opt => setFilters(prev => ({ ...prev, status: opt?.value || '' }))}
                                            styles={customSelectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ── Summary Cards ── */}
                        <div className="sales-summary-grid">
                            <div className="sales-summary-card card-appointments">
                                <div className="sales-card-icon">
                                    <Icon icon="mdi:calendar-check" />
                                </div>
                                <div className="sales-card-info">
                                    <span className="sales-card-label">Total Appointments</span>
                                    <span className="sales-card-value">{summary.totalAppointments || 0}</span>
                                </div>
                            </div>

                            {activeTab !== 'status-wise' && (
                                <>
                                    <div className="sales-summary-card card-value">
                                        <div className="sales-card-icon">
                                            <Icon icon="mdi:currency-inr" />
                                        </div>
                                        <div className="sales-card-info">
                                            <span className="sales-card-label">Total Value</span>
                                            <span className="sales-card-value">{Number(summary.totalServiceValue || 0).toFixed(0)}</span>
                                        </div>
                                    </div>
                                    <div className="sales-summary-card card-avg">
                                        <div className="sales-card-icon">
                                            <Icon icon="mdi:chart-line" />
                                        </div>
                                        <div className="sales-card-info">
                                            <span className="sales-card-label">Avg Value</span>
                                            <span className="sales-card-value">
                                                {Number(summary.avgAppointmentValue || 0).toFixed(0)}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="sales-summary-card card-completed">
                                <div className="sales-card-icon">
                                    <Icon icon="mdi:check-circle-outline" />
                                </div>
                                <div className="sales-card-info">
                                    <span className="sales-card-label">Completed</span>
                                    <span className="sales-card-value">{summary.completedCount || 0}</span>
                                </div>
                            </div>

                            <div className="sales-summary-card card-pending">
                                <div className="sales-card-icon">
                                    <Icon icon="mdi:clock-outline" />
                                </div>
                                <div className="sales-card-info">
                                    <span className="sales-card-label">Pending</span>
                                    <span className="sales-card-value">{summary.pendingCount || 0}</span>
                                </div>
                            </div>

                            <div className="sales-summary-card card-cancelled">
                                <div className="sales-card-icon">
                                    <Icon icon="mdi:close-circle-outline" />
                                </div>
                                <div className="sales-card-info">
                                    <span className="sales-card-label">Cancelled</span>
                                    <span className="sales-card-value">{summary.cancelledCount || 0}</span>
                                </div>
                            </div>
                        </div>

                        {/* ── Table ── */}
                        <div className="sales-table-container">
                            {/* Table Controls */}
                            <div className="table-header-row">
                                <h3 className="table-title">Appointment Details</h3>
                                <div className="table-controls">
                                    <div className="table-search-box">
                                        <input
                                            type="text"
                                            placeholder="Search in table..."
                                            value={tableSearch}
                                            onChange={e => setTableSearch(e.target.value)}
                                        />
                                        <Icon icon="mdi:magnify" />
                                    </div>
                                    <button className="table-control-btn" onClick={() => setTableSearch('')}>
                                        <Icon icon="mdi:filter-remove-outline" /> Clear
                                    </button>
                                    <div className="column-config-wrapper" ref={columnTogglerRef}>
                                        <button className="table-control-btn" onClick={() => setShowColumnToggler(v => !v)}>
                                            <Icon icon="mdi:view-column-outline" /> Columns
                                        </button>
                                        {showColumnToggler && (
                                            <div className="column-config-dropdown">
                                                <div className="dropdown-header">Toggle Columns</div>
                                                {(TAB_COLUMNS[activeTab] || []).map(col => (
                                                    <label key={col} className="column-config-item">
                                                        <input
                                                            type="checkbox"
                                                            checked={visibleColumns[col]}
                                                            onChange={() => setVisibleColumns(prev => ({ ...prev, [col]: !prev[col] }))}
                                                        />
                                                        <span>{COLUMN_LABELS[col]}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Table Body */}
                            <div className="sales-table-wrapper">
                                <div className="appointment-table-responsive">
                                    {loading ? (
                                        <div className="loading-state">
                                            <Icon icon="mdi:loading" className="spin" width="48" />
                                            <p>Loading appointment data...</p>
                                        </div>
                                    ) : data.length > 0 ? (
                                        <table className="sales-data-table">
                                            <thead>
                                                <tr>
                                                    {TAB_COLUMNS[activeTab].map(col =>
                                                        visibleColumns[col] && <th key={col}>{COLUMN_LABELS[col]}</th>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.map((row, idx) => (
                                                    <tr key={idx}>
                                                        {TAB_COLUMNS[activeTab].map(col => visibleColumns[col] && (
                                                            <td key={col}>
                                                                {(() => {
                                                                    let value = row[col];

                                                                    if (activeTab === 'overall') {
                                                                        if (col === 'appointmentDate') value = row.appointmentDate;
                                                                        if (col === 'clientName') value = row.customerName;
                                                                        if (col === 'clientPhone') value = row.customerPhone || row.clientPhone || '-';
                                                                        if (col === 'therapistName') value = Array.isArray(row.employeeNames) ? row.employeeNames.join(', ') : row.employeeNames;
                                                                        if (col === 'servicesProvided') value = Array.isArray(row.serviceNames) ? row.serviceNames.join(', ') : row.serviceNames;
                                                                        if (col === 'serviceValue') value = row.totalServiceValue;
                                                                        if (col === 'branchName') value = row.branchName;
                                                                        if (col === 'totalDuration') value = row.totalDuration || '-';
                                                                    } else if (activeTab === 'status-wise') {
                                                                        if (col === 'servicesProvided') value = Array.isArray(row.serviceNames) ? row.serviceNames.join(', ') : row.serviceNames;
                                                                        if (col === 'status') value = row.status;
                                                                        if (col === 'clientName') value = row.customerName;
                                                                    } else if (activeTab === 'therapist-wise') {
                                                                        if (col === 'therapistName') value = row.therapistName;
                                                                        if (col === 'totalAppointments') value = row.totalAppointments;
                                                                        if (col === 'totalServiceCount') value = row.totalServiceCount;
                                                                        if (col === 'serviceValue') value = row.serviceValue || 0;
                                                                        if (col === 'completedCount') value = row.completedCount;
                                                                    } else if (activeTab === 'service-wise') {
                                                                        if (col === 'serviceName') value = row.serviceName;
                                                                        if (col === 'categoryName') value = row.categoryName || '-';
                                                                        if (col === 'bookedCount') value = row.bookedCount;
                                                                        if (col === 'totalValue') value = row.totalValue;
                                                                    }

                                                                    if (col === 'status') {
                                                                        return (
                                                                            <span className="status-badge" style={{ backgroundColor: getStatusColor(value), color: getStatusTextColor(value) }}>
                                                                                {value}
                                                                            </span>
                                                                        );
                                                                    }
                                                                    if (col === 'serviceValue' || col === 'totalValue') {
                                                                        return (value);
                                                                    }
                                                                    if (col === 'action') {
                                                                        return (
                                                                            <button
                                                                                className="view-action-btn"
                                                                                onClick={() => { setSelectedRecord(row); setIsModalOpen(true); }}
                                                                                title="View Details"
                                                                            >
                                                                                <Icon icon="mdi:eye-outline" width="18" />
                                                                            </button>
                                                                        );
                                                                    }
                                                                    if (col === 'appointmentDate') {
                                                                        const d = row.timeSlot || row.appointmentDate;
                                                                        return d ? new Date(d).toLocaleString() : '-';
                                                                    }
                                                                    return value !== undefined && value !== null && value !== '' ? value : '-';
                                                                })()}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="no-data-state">
                                            <Icon icon="mdi:database-off-outline" width="56" />
                                            <p>No appointments found for the selected criteria</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Pagination */}
                            <div className="table-pagination-row">
                                <div className="pagination-info">
                                    Showing {data.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0} to {Math.min(currentPage * rowsPerPage, totalItems)} of {totalItems} entries
                                </div>
                                <div className="pagination-controls">
                                    <button className="pagination-arrow" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                                        <Icon icon="mdi:chevron-double-left" />
                                    </button>
                                    <button className="pagination-arrow" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                                        <Icon icon="mdi:chevron-left" />
                                    </button>

                                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) pageNum = i + 1;
                                        else if (currentPage <= 3) pageNum = i + 1;
                                        else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                        else pageNum = currentPage - 2 + i;
                                        return (
                                            <button
                                                key={pageNum}
                                                className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                                                onClick={() => setCurrentPage(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}

                                    <button className="pagination-arrow" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => p + 1)}>
                                        <Icon icon="mdi:chevron-right" />
                                    </button>
                                    <button className="pagination-arrow" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(totalPages)}>
                                        <Icon icon="mdi:chevron-double-right" />
                                    </button>
                                </div>
                                <div className="rows-per-page">
                                    <span>Rows per page:</span>
                                    <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* ── Appointment Details Modal ── */}
            {isModalOpen && selectedRecord && (
                <div className="appointment-modal-overlay" onClick={() => setIsModalOpen(false)}>
                    <div className="appointment-modal-content" onClick={e => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="appointment-modal-header">
                            <div className="modal-header-left">
                                <div className="modal-icon-badge">
                                    <Icon icon="mdi:calendar-account" width="22" />
                                </div>
                                <div>
                                    <h3>Appointment Details</h3>
                                    <p className="modal-subtitle">Full booking information</p>
                                </div>
                            </div>
                            <button className="modal-close-btn" onClick={() => setIsModalOpen(false)}>
                                <Icon icon="mdi:close" width="20" />
                            </button>
                        </div>

                        {/* Modal Body — renders only fields present in the response */}
                        <div className="appointment-modal-body">

                            {/* ── Status Banner (overall / status-wise) ── */}
                            {selectedRecord.status && (
                                <div className="modal-status-banner" style={{
                                    background: getStatusColor(selectedRecord.status),
                                    borderLeft: `4px solid ${getStatusTextColor(selectedRecord.status)}`
                                }}>
                                    <Icon icon="mdi:circle-medium" width="20" style={{ color: getStatusTextColor(selectedRecord.status) }} />
                                    <span style={{ color: getStatusTextColor(selectedRecord.status), fontWeight: 700 }}>
                                        {selectedRecord.status}
                                    </span>
                                </div>
                            )}

                            {/* ══════════════════════════════════
                                OVERALL / STATUS-WISE view
                                Fields: customerName, customerPhone, customerGender,
                                        timeSlot/appointmentDate, branchName, totalDuration,
                                        totalServiceValue, serviceNames, employeeNames
                               ══════════════════════════════════ */}
                            {(selectedRecord.customerName || selectedRecord.serviceNames) && (
                                <>
                                    <div className="modal-sections-grid">
                                        {/* Client Information */}
                                        <div className="modal-section">
                                            <div className="modal-section-title">
                                                <Icon icon="mdi:account-circle-outline" width="16" />
                                                Client Information
                                            </div>
                                            <div className="modal-detail-list">
                                                {(selectedRecord.customerName || selectedRecord.clientName) && (
                                                    <div className="modal-detail-row">
                                                        <span className="detail-label">Name</span>
                                                        <span className="detail-value">{selectedRecord.customerName || selectedRecord.clientName}</span>
                                                    </div>
                                                )}
                                                {(selectedRecord.customerPhone || selectedRecord.clientPhone) && (
                                                    <div className="modal-detail-row">
                                                        <span className="detail-label">Phone</span>
                                                        <span className="detail-value">{selectedRecord.customerPhone || selectedRecord.clientPhone}</span>
                                                    </div>
                                                )}
                                                {selectedRecord.customerGender && (
                                                    <div className="modal-detail-row">
                                                        <span className="detail-label">Gender</span>
                                                        <span className="detail-value">{selectedRecord.customerGender}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Appointment Info */}
                                        <div className="modal-section">
                                            <div className="modal-section-title">
                                                <Icon icon="mdi:calendar-clock-outline" width="16" />
                                                Appointment Info
                                            </div>
                                            <div className="modal-detail-list">
                                                {(selectedRecord.timeSlot || selectedRecord.appointmentDate) && (
                                                    <div className="modal-detail-row">
                                                        <span className="detail-label">Date & Time</span>
                                                        <span className="detail-value">
                                                            {new Date(selectedRecord.timeSlot || selectedRecord.appointmentDate).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                                                        </span>
                                                    </div>
                                                )}
                                                {selectedRecord.branchName && (
                                                    <div className="modal-detail-row">
                                                        <span className="detail-label">Branch</span>
                                                        <span className="detail-value">{selectedRecord.branchName}</span>
                                                    </div>
                                                )}
                                                {selectedRecord.totalDuration != null && (
                                                    <div className="modal-detail-row">
                                                        <span className="detail-label">Duration</span>
                                                        <span className="detail-value">{selectedRecord.totalDuration} min</span>
                                                    </div>
                                                )}
                                                {(selectedRecord.totalServiceValue != null || selectedRecord.totalValue != null) && (
                                                    <div className="modal-detail-row">
                                                        <span className="detail-label">Total Value</span>
                                                        <span className="detail-value highlight">
                                                            {(selectedRecord.totalServiceValue ?? selectedRecord.totalValue)}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Services & Staff Table — only shown for overall tab, hidden for status-wise tab as requested */}
                                    {activeTab !== 'status-wise' && Array.isArray(selectedRecord.serviceNames) && selectedRecord.serviceNames.length > 0 && (
                                        <div className="modal-section">
                                            <div className="modal-section-title">
                                                <Icon icon="mdi:spa-outline" width="16" />
                                                Services & Staff
                                            </div>
                                            <table className="modal-services-table">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Service Name</th>
                                                        <th>Therapist</th>
                                                        <th>Duration</th>
                                                        <th>Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedRecord.serviceNames.map((svc, i) => {
                                                        const count = selectedRecord.serviceNames.length || 1;
                                                        const staff = Array.isArray(selectedRecord.employeeNames)
                                                            ? (selectedRecord.employeeNames[i] || selectedRecord.employeeNames[0] || '-')
                                                            : (selectedRecord.employeeNames || '-');
                                                        const dur = selectedRecord.totalDuration ? Math.round(selectedRecord.totalDuration / count) : '-';
                                                        const val = (selectedRecord.totalServiceValue || 0) / count;
                                                        return (
                                                            <tr key={i}>
                                                                <td style={{ color: '#94a3b8', fontWeight: 600 }}>{i + 1}</td>
                                                                <td><strong>{svc}</strong></td>
                                                                <td>{staff}</td>
                                                                <td>{dur !== '-' ? `${dur} min` : '-'}</td>
                                                                <td style={{ color: '#16a34a', fontWeight: 700 }}>{(val)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr style={{ background: '#f8fafc' }}>
                                                        <td colSpan={4} style={{ fontWeight: 700, color: '#1e293b', paddingTop: 10 }}>Total</td>
                                                        <td style={{ fontWeight: 700, color: '#16a34a', paddingTop: 10 }}>
                                                            {(selectedRecord.totalServiceValue || selectedRecord.totalValue)}
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    )}

                                    {/* Simplified Services section for status-wise tab */}
                                    {activeTab === 'status-wise' && Array.isArray(selectedRecord.serviceNames) && selectedRecord.serviceNames.length > 0 && (
                                        <div className="modal-section">
                                            <div className="modal-section-title">
                                                <Icon icon="mdi:spa-outline" width="16" />
                                                Services
                                            </div>
                                            <div className="modal-detail-list">
                                                <div className="modal-detail-row">
                                                    <span className="detail-label">Service Names</span>
                                                    <span className="detail-value" style={{ textAlign: 'right' }}>
                                                        {selectedRecord.serviceNames.join(', ')}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* ══════════════════════════════════
                                THERAPIST-WISE view
                                Fields: therapistName, totalAppointments, totalServiceCount,
                                        completedCount, totalServiceValue
                               ══════════════════════════════════ */}
                            {selectedRecord.therapistName && !selectedRecord.customerName && (
                                <div className="modal-sections-grid">
                                    <div className="modal-section">
                                        <div className="modal-section-title">
                                            <Icon icon="mdi:account-tie-outline" width="16" />
                                            Therapist Summary
                                        </div>
                                        <div className="modal-detail-list">
                                            <div className="modal-detail-row">
                                                <span className="detail-label">Therapist</span>
                                                <span className="detail-value">{selectedRecord.therapistName}</span>
                                            </div>
                                            {selectedRecord.totalAppointments != null && (
                                                <div className="modal-detail-row">
                                                    <span className="detail-label">Total Appts</span>
                                                    <span className="detail-value">{selectedRecord.totalAppointments}</span>
                                                </div>
                                            )}
                                            {selectedRecord.totalServiceCount != null && (
                                                <div className="modal-detail-row">
                                                    <span className="detail-label">Services</span>
                                                    <span className="detail-value">{selectedRecord.totalServiceCount}</span>
                                                </div>
                                            )}
                                            {selectedRecord.completedCount != null && (
                                                <div className="modal-detail-row">
                                                    <span className="detail-label">Completed</span>
                                                    <span className="detail-value" style={{ color: '#16a34a' }}>{selectedRecord.completedCount}</span>
                                                </div>
                                            )}
                                            {(selectedRecord.totalServiceValue ?? selectedRecord.serviceValue) != null && (
                                                <div className="modal-detail-row">
                                                    <span className="detail-label">Total Value</span>
                                                    <span className="detail-value highlight">{(selectedRecord.totalServiceValue ?? selectedRecord.serviceValue)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ══════════════════════════════════
                                SERVICE-WISE view
                                Fields: serviceName, categoryName, bookedCount, totalValue
                               ══════════════════════════════════ */}
                            {selectedRecord.serviceName && !selectedRecord.customerName && (
                                <div className="modal-sections-grid">
                                    <div className="modal-section">
                                        <div className="modal-section-title">
                                            <Icon icon="mdi:spa-outline" width="16" />
                                            Service Summary
                                        </div>
                                        <div className="modal-detail-list">
                                            <div className="modal-detail-row">
                                                <span className="detail-label">Service</span>
                                                <span className="detail-value">{selectedRecord.serviceName}</span>
                                            </div>
                                            {selectedRecord.categoryName && (
                                                <div className="modal-detail-row">
                                                    <span className="detail-label">Category</span>
                                                    <span className="detail-value">{selectedRecord.categoryName}</span>
                                                </div>
                                            )}
                                            {selectedRecord.bookedCount != null && (
                                                <div className="modal-detail-row">
                                                    <span className="detail-label">Bookings</span>
                                                    <span className="detail-value">{selectedRecord.bookedCount}</span>
                                                </div>
                                            )}
                                            {selectedRecord.totalValue != null && (
                                                <div className="modal-detail-row">
                                                    <span className="detail-label">Total Value</span>
                                                    <span className="detail-value highlight">{(selectedRecord.totalValue)}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AppointmentReport;
